import PortalDocument, {PortalDocumentInterface} from "../../document/PortalDocument";
import AnnotationLayer from "./annotation/AnnotationLayer";
import {TextSearchGlobals} from "../../document/components/pdf/TextSearch";
import React, {createRef, ReactNode, RefObject} from "react";
import PageViewMode from "../settings/PageViewMode";
import DocumentSettings from "../../document/DocumentSettings";
import MouseMode from "../settings/MouseMode";
import {pdfjs} from "react-pdf";
import LengthUnit from "../../../shared/units/LengthUnit";
import TextLayer, {TextLayerGlobals} from "./TextLayer";
import PageLayer from "./PageLayer";
import {ViewerMode} from "../ViewerMode";
import ZoomLayer from "./ZoomLayer";
import AreaLayer from "./AreaLayer";
import Observer from "../../../shared/class/Observer";
import LocationEvent from "../LocationEvent";
import PortalContext from "../../PortalContext";
import MouseFunction from "../settings/MouseFunction";
import {Attachment} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Immutable from "../../../shared/immutable/Immutable";
import {I18nGlobals} from "../../../shared/translation/i18nInterface";
import {DebugGlobals} from "../../dialog/components/settings/DebugDialog";
import DestinationEvent from "../DestinationEvent";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import UserSettings from "../../session/UserSettings";
import UnitHelper from "../../../shared/helper/UnitHelper";
import {AreaLayerGlobals} from "./AreaLayerInterface";
import FormLayer from "./form/FormLayer";
import {FormLayerGlobals} from "./form/FormLayerInterface";
import {SidebarFunctionGlobals} from "../../document/components/pdf/SidebarFunctions";
import PortalPureComponent from "../../../shared/components/PortalPureComponent";

export interface PdfPageProperties extends TextSearchGlobals, I18nGlobals, AreaLayerGlobals, DebugGlobals,
	TextLayerGlobals, FormLayerGlobals, SidebarFunctionGlobals {
	pageCover: boolean;
	fullscreen: boolean;
	continuous: boolean;
	zoomValue: number;
	pageNumber: number;
	currentPage: number;
	attachmentKey?: string;
	context: PortalContext;
	pageMode: PageViewMode;
	mouseMode: MouseMode
	mouseFunction: MouseFunction;
	viewerMode: ViewerMode;
	locationObserver?: Observer<LocationEvent>;
	destinationObserver?: Observer<DestinationEvent>;
	intersectionObserver: IntersectionObserver;
	portalDocument: Immutable<PortalDocumentInterface>;
	updateCoordinates: (event: React.MouseEvent) => void;
	updateScroll: (xPos: number, yPos: number, pageNumber?: number) => Promise<void>;
	handleInteractivity?: () => void;
	interactivePage?: number;
	userSettings: Immutable<UserSettings>;
}

export interface PdfPageStatus {
	pageLayerWidth: number;
	pageLayerHeight: number;
	numberOfPages: number;
}

export class PdfPageDefinition extends PortalPureComponent<PdfPageProperties, PdfPageStatus> {
	private isClick: boolean;
	private pageLayer: RefObject<HTMLDivElement>;

	/**
	 * pdf page constructor
	 */
	constructor(props: Readonly<PdfPageProperties>) {
		super(props);

		this.isClick = false;
		this.pageLayer = createRef<HTMLDivElement>();

		this.state = {
			pageLayerWidth: 0,
			pageLayerHeight: 0,
			numberOfPages: 0
		}

		this.handleMouseDown = this.handleMouseDown.bind(this);
		this.handleMouseMove = this.handleMouseMove.bind(this);
		this.handleMouseUp = this.handleMouseUp.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		if (this.pageLayer.current !== null) {
			this.props.intersectionObserver.observe(this.pageLayer.current);
		}

		await this.updatePageDocument();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		if (this.pageLayer.current !== null) {
			this.props.intersectionObserver.unobserve(this.pageLayer.current);
		}
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PdfPageProperties>, prevState: Readonly<PdfPageStatus>
	): Promise<void> {
		await this.setNumberOfPages();

		if (this.props.portalDocument.get().id !== prevProps.portalDocument.get().id ||
			this.props.portalDocument.get().updated !== prevProps.portalDocument.get().updated ||
			this.props.portalResolution !== prevProps.portalResolution ||
			this.props.zoomValue !== prevProps.zoomValue || this.props.attachmentKey !== prevProps.attachmentKey) {
			await this.updatePageDocument();
		}
	}

	public static calculateRotationSteps = function (rotation: number): number {
		let temp: number = rotation;

		if (rotation < 0) {
			temp *= -1;
		}
		temp %= 360;
		if (rotation < 0) {
			temp = 360 - temp;
		}
		return Math.round(temp / 90);
	};

	private async updatePageDocument(): Promise<void> {
		try {
			await this.setNumberOfPages();

			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument.get().id!
			);

			let viewerData: pdfjs.PDFDocumentProxy;
			if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
				this.props.viewerMode === ViewerMode.PORTFOLIO ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
				this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
				let attachment: Attachment = portalDocument.getAttachment(this.props.attachmentKey!)!;
				viewerData = await portalDocument.loadViewerData(attachment);
			} else {
				viewerData = await portalDocument.loadViewerData();
			}

			if (!this.mounted) {
				return;
			}

			let pageProxy: pdfjs.PDFPageProxy = await viewerData.getPage(this.props.pageNumber);
			let scaledViewport: pdfjs.PageViewport = pageProxy.getViewport({scale: this.props.zoomValue / 100});

			this.setState({
				pageLayerWidth: UnitHelper.unitToPx(
					scaledViewport.width, LengthUnit.POINTS, this.props.portalResolution
				),
				pageLayerHeight: UnitHelper.unitToPx(
					scaledViewport.height, LengthUnit.POINTS, this.props.portalResolution
				)
			});
		} catch (e: any) {
			// can be ignored here
		}
	}

	private async setNumberOfPages(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let numberOfPages: number = 1;
		if (this.props.viewerMode === ViewerMode.ATTACHMENT ||
			this.props.viewerMode === ViewerMode.PORTFOLIO ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PREVIEW ||
			this.props.viewerMode === ViewerMode.PORTFOLIO_PAGE) {
			let attachment: Attachment | undefined =
				portalDocument.getAttachment(FunctionHelper.applyValue(this.props.attachmentKey, ""));

			if (FunctionHelper.exists(attachment)) {
				numberOfPages = await portalDocument.getNumberOfPages(attachment);
			}
		} else {
			numberOfPages = await portalDocument.getNumberOfPages();
		}

		this.setState({
			numberOfPages: numberOfPages
		});
	}

	/**
	 * Handle mouse down events
	 */
	private handleMouseDown(event: React.MouseEvent): void {
		this.isClick = true;
	}

	/**
	 * Handle mouse move events
	 * @private
	 */
	private handleMouseMove(event: React.MouseEvent) {
		this.isClick = false;
		this.props.updateCoordinates(event);
	}

	/**
	 * Handle mouse up events
	 */
	private async handleMouseUp(event: React.MouseEvent): Promise<void> {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (!this.props.fullscreen && event.button === 0 && this.isClick) {
			if (this.props.currentPage === this.props.pageNumber || this.props.mouseMode !== MouseMode.MOVE) {
				return;
			}

			documentSettings.activeViewer.currentPage = this.props.pageNumber;
			this.props.context.documentManager.updateGlobalDocumentList();
		}

		if (this.props.fullscreen && this.isClick) {
			if (event.button === 0) {
				documentSettings.activeViewer.nextPage(this.state.numberOfPages);
			} else if (event.button === 2) {
				documentSettings.activeViewer.previousPage();
			}

			this.props.context.documentManager.updateGlobalDocumentList();
		}

		this.isClick = false;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let pageClasses: Array<string> = ["page", "page-" + this.props.pageNumber];

		if (this.props.pageNumber === this.props.currentPage) {
			pageClasses.push("current");
		}

		if (this.props.pageMode === PageViewMode.DUAL &&
			(
				this.props.pageCover ?
					(this.props.pageNumber % 2 === 0 && this.props.currentPage - 1 === this.props.pageNumber) ||
					(this.props.pageNumber % 2 === 1 && this.props.currentPage + 1 === this.props.pageNumber) :
					(this.props.pageNumber % 2 === 0 && this.props.currentPage + 1 === this.props.pageNumber) ||
					(this.props.pageNumber % 2 === 1 && this.props.currentPage - 1 === this.props.pageNumber)
			)
		) {
			pageClasses.push("active");
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);

		let editPasswordNeeded: boolean = false;
		if (portalDocument.hasPasswordProtection("permission") &&
			(!portalDocument.getDocumentFile()?.password?.isPermissionSet || portalDocument.hasError()) &&
			!portalDocument.metadata?.security?.canExtractContent
		) {
			editPasswordNeeded = true;
		}

		return (
			<div className={pageClasses.join(" ")} onPointerDown={this.handleMouseDown}
				 onPointerMove={this.handleMouseMove} onPointerUp={this.handleMouseUp}>
				<div className="page-layer" data-page={this.props.pageNumber} ref={this.pageLayer} style={
					{
						width: this.state.pageLayerWidth + LengthUnit.PIXEL,
						height: this.state.pageLayerHeight + LengthUnit.PIXEL
					}
				}>
					<PageLayer {...this.props}/>
				</div>
				{(this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
					!editPasswordNeeded &&
                    <TextLayer {...this.props}/>
				}
				{this.props.viewerMode !== ViewerMode.PORTFOLIO && this.props.viewerMode !== ViewerMode.PORTFOLIO_PAGE &&
					this.props.viewerMode !== ViewerMode.PORTFOLIO_PREVIEW &&
                    <AnnotationLayer {...this.props} numberOfPages={this.state.numberOfPages}/>
				}
				{this.props.viewerMode === ViewerMode.DEFAULT &&
                    <FormLayer {...this.props} numberOfPages={this.state.numberOfPages}/>
				}
				{(this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
                    <ZoomLayer {...this.props}/>
				}
				{(this.props.viewerMode === ViewerMode.DEFAULT || this.props.viewerMode === ViewerMode.PORTFOLIO) &&
                    <AreaLayer {...this.props}/>
				}
			</div>
		);
	}
}

const PdfPage = React.memo(PdfPageDefinition,
	(prevProps: Readonly<PdfPageProperties>, nextProps: Readonly<PdfPageProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSelection) &&
			!nextProps.textSelection!.equals(prevProps.textSelection)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSearchResults) &&
			!nextProps.textSearchResults!.equals(prevProps.textSearchResults)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.formValues) && !nextProps.formValues!.equals(prevProps.formValues)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.userSettings) && !nextProps.userSettings.equals(prevProps.userSettings)) {
			return false;
		}

		if (nextProps.attachmentKey !== prevProps.attachmentKey) {
			return false;
		}

		if (nextProps.viewerMode !== prevProps.viewerMode) {
			return false;
		}

		if (nextProps.currentPage !== prevProps.currentPage) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		if (nextProps.pageMode !== prevProps.pageMode) {
			return false;
		}

		if (nextProps.pageCover !== prevProps.pageCover) {
			return false;
		}

		if (nextProps.mouseMode !== prevProps.mouseMode) {
			return false;
		}

		if (nextProps.textSearchSelection !== prevProps.textSearchSelection) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.mouseFunctionAnnotationParameter) &&
			!nextProps.mouseFunctionAnnotationParameter!.equals(prevProps.mouseFunctionAnnotationParameter)) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		if (nextProps.interactivePage !== prevProps.interactivePage) {
			return false;
		}

		if (nextProps.signatureEditProfile !== prevProps.signatureEditProfile) {
			return false;
		}

		if (nextProps.portalResolution !== prevProps.portalResolution) {
			return false;
		}

		return true;
	}
);

export default PdfPage;